import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { Bell, Save, Check, Mail, MailWarning, Send, Loader2, Sparkles, CheckCircle2, XCircle } from 'lucide-react';
import { t } from '../../lib/i18n';
import { useReminders } from '../../hooks/useReminders';

const FREQUENCIES = ['daily', 'weekly', 'biweekly'];

const ReminderRow = ({ reminder, index, language, onChange }) => (
  <div className="flex flex-wrap items-center gap-3 sm:gap-4 p-3 rounded-lg bg-black/20 border border-white/5" data-testid={`reminder-row-${reminder.category}`}>
    <Switch
      data-testid={`notifications-${reminder.category}-switch`}
      checked={reminder.enabled}
      onCheckedChange={v => onChange(index, 'enabled', v)}
    />
    <div className="flex-1 min-w-[120px]">
      <p className="text-sm text-white">{t(reminder.category, language)}</p>
    </div>
    <div className="flex items-center gap-2 w-full sm:w-auto pl-12 sm:pl-0">
      <Select value={reminder.frequency} onValueChange={v => onChange(index, 'frequency', v)}>
        <SelectTrigger data-testid={`reminder-frequency-${reminder.category}`} className="w-28 sm:w-32 bg-black/20 border-white/10 text-white text-xs h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[var(--lux-panel)] border-white/10">
          {FREQUENCIES.map(f => (
            <SelectItem key={f} value={f} className="text-white hover:bg-white/5 text-xs">{t(f, language)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <input
        type="time"
        data-testid={`reminder-time-${reminder.category}`}
        value={reminder.time}
        onChange={e => onChange(index, 'time', e.target.value)}
        className="bg-black/20 border border-white/10 rounded px-2 py-1 text-white text-xs h-8 min-w-0"
      />
    </div>
  </div>
);

const EmailStatusNotice = ({ emailStatus, timezone, language }) => {
  if (!emailStatus) return null;
  if (emailStatus.is_guest) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-lg bg-lux-gold/10 border border-lux-gold/25 text-xs text-[var(--lux-amber)]" data-testid="reminder-guest-notice">
        <MailWarning className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>{t('reminderGuestNotice', language)}</span>
      </div>
    );
  }
  if (!emailStatus.email_configured) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-lg bg-white/5 border border-white/10 text-xs text-white/60" data-testid="reminder-not-configured-notice">
        <MailWarning className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>{t('reminderNotConfigured', language)}</span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-white/5 border border-white/10 text-xs text-white/60" data-testid="reminder-email-notice">
      <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-[var(--lux-gold)]" />
      <span>
        {t('reminderEmailTo', language)} <span className="text-white font-medium" data-testid="reminder-email-address">{emailStatus.email}</span>
        {' · '}{t('reminderFrequencyHelp', language)} ({timezone}).
      </span>
    </div>
  );
};

const RecentLogs = ({ logs, language }) => {
  if (!logs.length) return null;
  const locale = language === 'fr' ? 'fr-FR' : 'en-US';
  return (
    <div className="mt-4" data-testid="reminder-logs">
      <p className="text-xs text-white/40 uppercase tracking-wider mb-2">{t('reminderLastSent', language)}</p>
      <ul className="space-y-1.5">
        {logs.slice(0, 5).map(log => (
          <li key={log._id} className="flex items-center gap-2 text-xs text-white/60">
            {log.status === 'sent'
              ? <CheckCircle2 className="w-3.5 h-3.5 text-[var(--lux-gold)]" />
              : <XCircle className="w-3.5 h-3.5 text-[var(--lux-danger)]" />}
            <span className="text-white">{t(log.category, language)}</span>
            <span>·</span>
            <span>{log.sent_at ? new Date(log.sent_at).toLocaleString(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
            <span>·</span>
            <span>{log.status === 'sent' ? t('reminderStatusSent', language) : t('reminderStatusFailed', language)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const RemindersCard = ({ language }) => {
  const { reminders, emailStatus, logs, loading, saving, testing, updateReminder, save, sendTest, timezone } = useReminders();
  const [saved, setSaved] = useState(false);
  const [testCategory, setTestCategory] = useState('checkin');

  const handleSave = async () => {
    try {
      await save();
      setSaved(true);
      toast.success(t('saved', language));
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      toast.error(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Error saving reminders');
    }
  };

  const handleTest = async () => {
    try {
      await sendTest(testCategory);
      toast.success(t('reminderTestSent', language));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'guest_no_email') toast.error(t('reminderGuestNotice', language));
      else if (detail === 'email_not_configured') toast.error(t('reminderNotConfigured', language));
      else toast.error(t('reminderTestError', language));
    }
  };

  const canSendEmail = Boolean(emailStatus?.can_receive_email);

  return (
    <Card className="bg-lux-panel/90 border-white/[0.08]" data-testid="settings-reminders-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <Bell className="w-5 h-5 text-[var(--lux-gold)]" />
          {t('notificationSettings', language)}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <EmailStatusNotice emailStatus={emailStatus} timezone={timezone} language={language} />

        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map(i => <Skeleton key={i} className="h-14 w-full bg-white/5" />)}
          </div>
        ) : (
          <div className="space-y-3" data-testid="notifications-category-list">
            {reminders.map((reminder, i) => (
              <ReminderRow key={reminder.category} reminder={reminder} index={i} language={language} onChange={updateReminder} />
            ))}
          </div>
        )}

        <p className="flex items-center gap-1.5 text-xs text-white/40">
          <Sparkles className="w-3.5 h-3.5 text-[var(--lux-gold)]" />
          {t('reminderAiNote', language)}
        </p>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button
            data-testid="settings-save-button"
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] active:scale-[0.98] transition-[background-color,transform] duration-200"
          >
            {saved ? <Check className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {saved ? t('saved', language) : t('save', language)}
          </Button>

          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <Select value={testCategory} onValueChange={setTestCategory}>
              <SelectTrigger data-testid="reminder-test-category" className="w-28 sm:w-32 bg-black/20 border-white/10 text-white text-xs h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--lux-panel)] border-white/10">
                {reminders.map(r => (
                  <SelectItem key={r.category} value={r.category} className="text-white hover:bg-white/5 text-xs">{t(r.category, language)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              data-testid="reminder-test-send-button"
              onClick={handleTest}
              disabled={testing || !canSendEmail}
              variant="outline"
              className="bg-[var(--lux-panel)] border-white/10 text-white hover:bg-[var(--lux-panel-2)] hover:text-[var(--lux-gold)] h-9"
              title={!canSendEmail && emailStatus?.is_guest ? t('reminderGuestNotice', language) : undefined}
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {testing ? t('reminderTestSending', language) : t('reminderTestSend', language)}
            </Button>
          </div>
        </div>

        <RecentLogs logs={logs} language={language} />
      </CardContent>
    </Card>
  );
};
